import {
  BarChart3,
  Bike,
  Calendar,
  CalendarDays,
  CalendarOff,
  ChevronDown,
  ClipboardList,
  DollarSign,
  FileText,
  Home,
  LogOut,
  Mail,
  Package,
  ShieldCheck,
  Tag,
  Truck,
  Users,
  Wrench
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getVersionInfo } from '../config/app';
import { useAuth } from '../contexts/AuthContext';

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';
const ADMIN_GROUP_OPEN_KEY = 'sidebarAdminGroupOpen';
const AUTO_COLLAPSE_MAX_WIDTH = 1440;

// Preferencia guardada; si no hay, colapsar por defecto en pantallas estrechas
const getInitialCollapsed = () => {
  try {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (saved !== null) {
      return saved === 'true';
    }
  } catch {
    // localStorage no disponible
  }
  return window.innerWidth < AUTO_COLLAPSE_MAX_WIDTH;
};

const getInitialAdminGroupOpen = () => {
  try {
    return localStorage.getItem(ADMIN_GROUP_OPEN_KEY) === 'true';
  } catch {
    return false;
  }
};

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsed);
  const [isAdminGroupOpen, setIsAdminGroupOpen] = useState(getInitialAdminGroupOpen);

  // Atajo Ctrl+B / Cmd+B para colapsar/expandir
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        setIsCollapsed((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
    } catch {
      // localStorage no disponible
    }
  }, [isCollapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(ADMIN_GROUP_OPEN_KEY, String(isAdminGroupOpen));
    } catch {
      // localStorage no disponible
    }
  }, [isAdminGroupOpen]);

  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  // Menú completo para usuarios no-AGENCY
  const allMenuItems = [
    // { path: '/', icon: Home, label: 'Inicio' },
    { path: '/calendario', icon: Calendar, label: 'Calendario' },
    { path: '/bicicletas', icon: Bike, label: 'Vehículos' },
    { path: '/reservas', icon: Calendar, label: 'Reservas' },
    { path: '/contratos', icon: FileText, label: 'Contratos' },
    { path: '/entregas-recogidas', icon: Truck, label: 'Entregas/Recogidas' },
    { path: '/preparacion-dia', icon: ClipboardList, label: 'Preparación del día' },
    { path: '/accesorios', icon: Package, label: 'Accesorios' },
    { path: '/clientes', icon: Users, label: 'Clientes' },
    { path: '/mantenimiento', icon: Wrench, label: 'Mantenimiento' },
    { path: '/models', icon: Bike, label: 'Modelos' },
    { path: '/categorias', icon: Tag, label: 'Categorías' },
    { path: '/temporadas', icon: CalendarDays, label: 'Temporadas' },
    { path: '/precios-estacionales', icon: DollarSign, label: 'Precios Estacionales' },
    // { path: '/actividades', icon: CalendarDays, label: 'Actividades' },
    // { path: '/ajustes', icon: Settings, label: 'Ajustes' }, // Oculto por el momento
    // { path: '/email-preview', icon: Mail, label: 'Vista Previa Email' },
  ];

  // Vistas de uso puntual agrupadas en un desplegable (solo ADMIN)
  const adminGroupItems = [
    { path: '/informes', icon: BarChart3, label: 'Informes' },
    { path: '/usuarios', icon: Users, label: 'Usuarios' },
    { path: '/dias-cerrados', icon: CalendarOff, label: 'Días cerrados' },
  ];

  // Menú limitado para usuarios AGENCY (solo Reservas)
  const agencyMenuItems = [
    { path: '/reservas', icon: Calendar, label: 'Reservas' },
  ];

  // Seleccionar menú según el rol del usuario
  const menuItems = user?.role === 'AGENCY' ? agencyMenuItems : allMenuItems;

  const isActive = (path) => {
    if (!location?.pathname) {
      return false;
    }
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const showAdminGroup = user?.role === 'ADMIN';
  const isAdminGroupActive = adminGroupItems.some((item) => isActive(item.path));

  // Abrir el grupo automáticamente si se navega a una de sus vistas
  useEffect(() => {
    if (isAdminGroupActive) {
      setIsAdminGroupOpen(true);
    }
  }, [isAdminGroupActive]);

  const versionInfo = getVersionInfo(isCollapsed);

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Logo MonkeyBike */}
      <div className="sidebar-logo">
        <div className="logo-container">
          <img
            src="https://bikerentmallorca.com/wp-content/uploads/2025/04/logo-monkey-bikes-mallorca.png"
            alt="MonkeyBike"
            className={`sidebar-logo-img ${isCollapsed ? 'collapsed' : ''}`}
          />
        </div>
      </div>

      {/* Menú de navegación */}
      <nav className="sidebar-nav">
        <ul className="nav-list">
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <li key={item.path} className="nav-item">
                <Link
                  to={item.path}
                  className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                  title={isCollapsed ? item.label : ''}
                >
                  <IconComponent size={20} />
                  {!isCollapsed && <span>{item.label}</span>}
                </Link>
              </li>
            );
          })}

          {showAdminGroup && (
            <li className="nav-item nav-group">
              <button
                type="button"
                className={`nav-link nav-group-toggle ${isAdminGroupActive && !isAdminGroupOpen ? 'active' : ''}`}
                onClick={() => setIsAdminGroupOpen((prev) => !prev)}
                title={isCollapsed ? 'Administración' : ''}
                aria-expanded={isAdminGroupOpen}
              >
                <ShieldCheck size={20} />
                {!isCollapsed && <span>Administración</span>}
                <ChevronDown
                  size={16}
                  className={`nav-group-chevron ${isAdminGroupOpen ? 'open' : ''}`}
                />
              </button>
              {isAdminGroupOpen && (
                <ul className="nav-group-list">
                  {adminGroupItems.map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <li key={item.path} className="nav-item">
                        <Link
                          to={item.path}
                          className={`nav-link nav-group-link ${isActive(item.path) ? 'active' : ''}`}
                          title={isCollapsed ? item.label : ''}
                        >
                          <IconComponent size={18} />
                          {!isCollapsed && <span>{item.label}</span>}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          )}
        </ul>
      </nav>

      {/* Sección de usuario */}
      <div className="sidebar-footer">
        <div className="user-section">
          {/* Mi cuenta oculto por el momento
          <Link
            to="/mi-cuenta"
            className={`nav-link ${isActive('/mi-cuenta') ? 'active' : ''}`}
            title={isCollapsed ? 'Mi cuenta' : ''}
          >
            <User size={20} />
            {!isCollapsed && <span>Mi cuenta</span>}
          </Link>
          */}
          <button 
            className="logout-button"
            title={isCollapsed ? 'Cerrar sesión' : ''}
            onClick={() => {
              logout();
              navigate('/login');
            }}
          >
            <LogOut size={20} />
            {!isCollapsed && <span>Cerrar sesión</span>}
          </button>
        </div>
        
        {/* Información de versión y entorno */}
        <div className="version-info">
          <span className="version-text" title={isCollapsed ? versionInfo.tooltip : ''}>
            {versionInfo.fullText}
          </span>
        </div>
      </div>

      {/* Botón para colapsar/expandir */}
      <button 
        className="sidebar-toggle"
        onClick={() => setIsCollapsed(!isCollapsed)}
        title={`${isCollapsed ? 'Expandir menú' : 'Colapsar menú'} (Ctrl/⌘+B)`}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {isCollapsed ? (
            <path d="M9 18l6-6-6-6"/>
          ) : (
            <path d="M15 18l-6-6 6-6"/>
          )}
        </svg>
      </button>
    </div>
  );
};

export default Sidebar; 